import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';
import { apiErrorMessage } from '../utils/apiError';

const isBlank = (value) => value === '' || value === null || value === undefined;

const dateBounds = (from, to, formatTo) => {
  const params = {};
  if (from) params['filter[created_at][from]'] = from;
  if (to) params['filter[created_at][to]'] = formatTo(to);
  return params;
};

// Most lists compare filter[created_at][to] verbatim, so a bare date would drop that whole day.
export const endOfDayDates = ({ from, to }) => dateBounds(from, to, (day) => `${day} 23:59:59`);

// The ledger and withdrawal lists round to whole days themselves.
export const wholeDayDates = ({ from, to }) => dateBounds(from, to, (day) => day);

const buildParams = ({ page, pageSize, sort, search, filters, from, to, dateParams, extraParams }) => {
  const params = { page: page + 1, perPage: pageSize };

  if (sort) params.sort = sort;
  if (search) params.search = search;

  Object.entries(filters).forEach(([field, value]) => {
    if (!isBlank(value)) params[`filter[${field}]`] = value;
  });

  Object.entries(extraParams).forEach(([name, value]) => {
    if (!isBlank(value)) params[name] = value;
  });

  return { ...params, ...dateParams({ from, to }) };
};

const unwrapPage = (body) => {
  const envelope = body?.data ?? {};
  return {
    envelope,
    rows: Array.isArray(envelope.data) ? envelope.data : [],
    total: envelope.total ?? 0,
    pageCount: envelope.last_page ?? 0,
  };
};

export const useServerTable = (
  endpoint,
  {
    sort: initialSort = '',
    search: initialSearch = '',
    filters: initialFilters = {},
    pageSize: initialPageSize = 10,
    enabled = true,
    fallbackError = 'We could not load this list. Please try again.',
    dateParams = endOfDayDates,
    extraParams = {},
  } = {}
) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [sort, setSortState] = useState(initialSort);
  const [search, setSearchState] = useState(initialSearch);
  const [filters, setFilters] = useState(initialFilters);
  const [from, setFromState] = useState('');
  const [to, setToState] = useState('');

  // Keyed by content so a caller may pass a fresh object literal on every render.
  const extraKey = JSON.stringify(extraParams);
  const [appliedExtraKey, setAppliedExtraKey] = useState(extraKey);
  if (appliedExtraKey !== extraKey) {
    setAppliedExtraKey(extraKey);
    setPage(0);
  }

  const [rows, setRows] = useState([]);
  const [envelope, setEnvelope] = useState(null);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const latestRequest = useRef(0);
  const silentReload = useRef(false);
  const lastRequestedParams = useRef(null);

  const invalidDateRange = Boolean(from && to && from > to);

  const params = useMemo(
    () =>
      buildParams({ page, pageSize, sort, search, filters, from, to, dateParams, extraParams: JSON.parse(extraKey) }),
    [page, pageSize, sort, search, filters, from, to, dateParams, extraKey]
  );

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);
  // Polling uses this: the current rows stay on screen instead of flashing the loading skeleton.
  const refetchSilently = useCallback(() => {
    silentReload.current = true;
    setReloadToken((token) => token + 1);
  }, []);

  const setFilter = useCallback((field, value) => {
    // Preserving identity when nothing changed keeps the params memo stable, so a control that
    // re-emits its current value on mount does not fire a redundant request.
    setFilters((current) => (current[field] === value ? current : { ...current, [field]: value }));
    setPage(0);
  }, []);

  const setSearch = useCallback((value) => {
    setSearchState(value);
    setPage(0);
  }, []);

  const setSort = useCallback((value) => {
    setSortState(value);
    setPage(0);
  }, []);

  const setPageSize = useCallback((value) => {
    setPageSizeState(value);
    setPage(0);
  }, []);

  const setFrom = useCallback((value) => {
    setFromState(value);
    setPage(0);
  }, []);

  const setTo = useCallback((value) => {
    setToState(value);
    setPage(0);
  }, []);

  useEffect(() => {
    // A query change landing in the same commit as a poll is a real load, not a silent one.
    const silent = silentReload.current && lastRequestedParams.current === params;
    silentReload.current = false;
    lastRequestedParams.current = params;

    if (!enabled || invalidDateRange) {
      setRows([]);
      setEnvelope(null);
      setTotal(0);
      setPageCount(0);
      setLoading(false);
      return;
    }

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    if (!silent) {
      setLoading(true);
      setError(null);
      setRequestError(null);
    }

    api
      .get(endpoint, { params })
      .then((response) => {
        if (latestRequest.current !== requestId) return;
        const result = unwrapPage(response?.data);
        setError(null);
        setRequestError(null);
        setEnvelope(result.envelope);
        setRows(result.rows);
        setTotal(result.total);
        setPageCount(result.pageCount);
        setLoading(false);
      })
      .catch((failure) => {
        if (latestRequest.current !== requestId) return;
        setRequestError(failure);
        // A failed poll keeps the last good rows; the next tick retries.
        if (silent) return;
        setRows([]);
        setEnvelope(null);
        setTotal(0);
        setPageCount(0);
        setError(apiErrorMessage(failure, fallbackError));
        setLoading(false);
      });
  }, [endpoint, params, enabled, invalidDateRange, reloadToken, fallbackError]);

  return {
    rows,
    envelope,
    total,
    pageCount,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    setSearch,
    sort,
    setSort,
    filters,
    setFilter,
    from,
    to,
    setFrom,
    setTo,
    invalidDateRange,
    params,
    loading,
    error,
    requestError,
    refetch,
    refetchSilently,
  };
};

export default useServerTable;
